import React, { FC, ReactNode } from 'react';
import { FetchMoreOptions } from 'apollo-client';

import { LoaderTypes } from 'types/app';
import {
  useGetContentEmailSpecsQuery,
  ContentEmailSpecFragment,
  GetContentEmailSpecsDocument,
  GetContentEmailSpecsQueryVariables,
} from 'api';
import { QueryManager } from 'components';

export interface IGetContentEmailSpecsResult {
  items: ContentEmailSpecFragment[];
  loading: boolean;
  paginate: boolean;
  onLoadMore: () => void;
}

interface IProps {
  loader?: LoaderTypes;
  variables: GetContentEmailSpecsQueryVariables;
  children: (result: IGetContentEmailSpecsResult) => ReactNode;
}

const GetContentEmailSpecs: FC<IProps> = ({ children, loader, variables }) => {
  const queryResult = useGetContentEmailSpecsQuery({
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const { loading, data, error, fetchMore } = queryResult;

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, { fetchMoreResult }) => {
    if (!fetchMoreResult) {
      return prev;
    }

    const prevResult = prev.getContentEmailSpecs;
    const nextResult = fetchMoreResult.getContentEmailSpecs;

    return {
      getContentEmailSpecs: {
        ...fetchMoreResult.getContentEmailSpecs,
        items: [...prevResult.items, ...nextResult.items],
      },
    };
  };

  function handleLoadMore() {
    const { id, created_at, paginate } = data.getContentEmailSpecs;

    if (loading || error || !paginate) {
      return Promise.resolve();
    }

    return fetchMore({
      query: GetContentEmailSpecsDocument,
      variables: { data: { ...variables.data, id, created_at } },
      updateQuery,
    });
  }

  return (
    <QueryManager loader={loader} {...queryResult}>
      {() => {
        if (!data?.getContentEmailSpecs) {
          return null;
        }

        const { items, paginate } = data.getContentEmailSpecs;

        return children({
          items,
          loading,
          paginate,
          onLoadMore: handleLoadMore,
        });
      }}
    </QueryManager>
  );
};

export default GetContentEmailSpecs;
